import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "../components/layout/SiteLayout";
import { APPS } from "../data/apps";
import { SITE, mailto } from "../data/site";
import { CAPABILITIES, CAREER_CLUSTER, CAREER_LOG, CAREER_TOTAL, HERO, SKILLS } from "../data/profile";
import { PRINCIPLES } from "../data/principles";
import { pickHomeApps } from "../lib/appQuery";
import { AppData } from "../types/app";
import "../styles/home.css";

const EMPTY = "준비 중인 앱이 있습니다";
// 앱 라벨은 지표가 있으면 지표, 심사 중이면 상태, 아니면 카테고리
const appLabel = (a: AppData) => a.metric ?? (a.status === "review" ? "스토어 심사 중" : a.category);
// 홈 career 클러스터의 회사 칸을 경력 로그의 같은 회사 브랜치로 잇는다
const careerHref = (name: string) => {
  const i = CAREER_LOG.findIndex((b) => name.startsWith(b.company));
  return i < 0 ? "/#career" : `/#career-${i}`;
};

function Photo() {
  return (
    <span className="home-photo">
      <img src="/kbl-640.jpg" alt="이광복 사진" width={640} height={896} loading="lazy" decoding="async" />
    </span>
  );
}

function AppCard({ app, primary }: { app: AppData; primary: boolean }) {
  return (
    <Link to={`/apps/${app.slug}`} className={primary ? "home-card home-card--pinned" : "home-card home-card--new"}>
      <span className="mono home-card-tag">{[appLabel(app), app.platforms.join(", ")].join(", ")}</span>
      <strong className="home-card-name">{app.name}</strong>
      <span className="home-card-summary">{app.summary}</span>
    </Link>
  );
}

export default function HomePage() {
  useEffect(() => {
    document.title = "이광복 | AI 네이티브 엔지니어";
  }, []);

  const { pinned, latest, total } = pickHomeApps(APPS);
  const [current, ...past] = CAREER_CLUSTER;

  return (
    <SiteLayout crumb="architecture" dark>
      <section className="home-hero">
        <div className="wrap home-hero-row">
          <div className="home-hero-title">
            <span className="mono home-hero-eyebrow">{HERO.eyebrow}</span>
            <h1>
              {HERO.headline[0]}
              <br className="home-br" /> {HERO.headline[1]}
            </h1>
          </div>
          <div className="home-hero-aside">
            <p>{HERO.intro}</p>
            <div className="home-hero-btns">
              <a href={SITE.resume_pdf} download className="home-btn home-btn--light">이력서 PDF</a>
              <a href={SITE.portfolio_pdf} download className="home-btn home-btn--outline">포트폴리오 PDF</a>
              <a href={mailto()} className="home-btn home-btn--outline">연락하기</a>
            </div>
          </div>
        </div>
      </section>

      <section className="wrap home-diagram" aria-label="시스템 다이어그램">
        <ul className="home-skills">
          {SKILLS.map((s) => (
            <li key={s.key} className="home-skill">
              <Link to={`/#cap-${s.key}`} className="home-hit" aria-label={`${s.value}, 전문성 보기`} />
              <span className="mono home-node-key">{s.key}</span>
              <strong>{s.value}</strong>
            </li>
          ))}
        </ul>
        <div className="home-trunk" aria-hidden="true" />

        <div className="home-clusters">
          <div className="home-cluster home-cluster--career">
            <span className="mono home-cluster-head">
              <span>cluster: career</span>
              <span>recent 3</span>
            </span>
            <Link to={careerHref(current.name)} className="home-job home-job--current">
              <span className="home-job-row">
                <strong>{current.name}</strong>
                <span className="mono">{current.period}</span>
              </span>
              <span className="home-job-highlight">{current.highlight}</span>
            </Link>
            {past.map((c) => (
              <Link key={c.name} to={careerHref(c.name)} className="home-job">
                <span className="home-job-row">
                  <strong>{c.name}</strong>
                  <span className="mono muted">{c.period}</span>
                </span>
                <span className="home-job-highlight">{c.highlight}</span>
              </Link>
            ))}
            <Link to="/#career" className="home-app-summary">
              <span>
                {CAREER_TOTAL.since}부터, 경력 {CAREER_TOTAL.years}년
              </span>
              <span className="mono">전체 경력</span>
            </Link>
          </div>

          <div className="home-core-col">
            <span className="home-link" aria-hidden="true" />
            <div className="home-core-stack">
            <a href={SITE.resume_pdf} target="_blank" rel="noopener noreferrer" className="home-core">
              <Photo />
              <span className="home-core-text">
                <span className="mono">core</span>
                <strong>{SITE.owner}</strong>
                <span className="mono home-core-cta">이력서 보기</span>
              </span>
              <span className="sr-only"> (이력서 PDF, 새 창)</span>
            </a>
            {/* core 아래 노드. 문서, 연락처, LinkedIn으로 바로 잇는다 */}
            <ul className="home-core-sub">
              <li>
                <a href={SITE.portfolio_pdf} target="_blank" rel="noopener noreferrer" className="home-sub">
                  <span className="mono">portfolio.pdf</span>
                  <strong>서비스 설계 노트</strong>
                  <span className="sr-only"> (새 창)</span>
                </a>
              </li>
              <li>
                <a href={mailto()} className="home-sub">
                  <span className="mono">contact</span>
                  <strong>{SITE.email}</strong>
                </a>
              </li>
              <li>
                <a href={SITE.linkedin} target="_blank" rel="noopener noreferrer" className="home-sub">
                  <span className="mono">linkedin</span>
                  <strong>in/siu9068</strong>
                  <span className="sr-only"> (새 창)</span>
                </a>
              </li>
            </ul>
            </div>
            <span className="home-link" aria-hidden="true" />
          </div>

          <div className="home-cluster">
            <span className="mono home-cluster-head">
              <span>cluster: principles</span>
              <span>{PRINCIPLES.length}</span>
            </span>
            {PRINCIPLES.map((p, i) => (
              <Link key={p.id} to={`/principles#${p.id}`} className="home-rule">
                <span className="mono">{String(i + 1).padStart(2, "0")}</span>
                <strong>{p.title}</strong>
              </Link>
            ))}
            <Link to="/principles" className="home-app-summary">
              <span>AI와 같이 개발하는 기준</span>
              <span className="mono">전체 원칙</span>
            </Link>
          </div>
        </div>
      </section>

      <section id="career" className="wrap home-section">
        <div className="home-section-head">
          <span className="eyebrow">$ git log --author=kwangbok</span>
          <h2>경력</h2>
          <p className="muted">회사를 브랜치로, 성과를 커밋으로 적었습니다.</p>
        </div>

        <ol className="home-log home-only-wide">
          {CAREER_LOG.map((b, i) => (
            <li key={b.company} id={`career-${i}`}>
              <div className="home-branch">
                <span className="home-branch-dot" style={{ background: b.dot }} aria-hidden="true" />
                <strong>{b.company}</strong>
                <span className={i === 0 ? "mono home-branch-meta is-current" : "mono home-branch-meta"} style={{ borderColor: b.dot }}>
                  {b.meta}
                </span>
              </div>
              <ul className="home-commits">
                {b.commits.map((c) => (
                  <li key={c.title} className="home-commit">
                    <span className="home-rail" aria-hidden="true" />
                    <span className="home-commit-body">
                      <span className="mono home-commit-type">{c.type}</span>
                      <span>{c.title}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ol>

        <div className="home-log-fold home-only-narrow">
          {CAREER_LOG.map((b, i) => (
            <details key={b.company} open={i < 2} data-anchor={`career-${i}`}>
              <summary>
                <span className="home-branch-dot" style={{ background: b.dot }} aria-hidden="true" />
                <span className="home-fold-title">
                  <strong>{b.company}</strong>
                  <span className="mono muted">{b.meta}</span>
                </span>
                <span className="mono muted home-fold-count">{b.commits.length} {b.commits.length === 1 ? "commit" : "commits"}</span>
              </summary>
              <ul className="home-commits">
                {b.commits.map((c) => (
                  <li key={c.title} className="home-commit">
                    <span className="home-rail" aria-hidden="true" />
                    <span className="home-commit-body">
                      <span className="mono home-commit-type">{c.type}</span>
                      <span>{c.title}</span>
                    </span>
                  </li>
                ))}
              </ul>
            </details>
          ))}
        </div>
      </section>

      <section id="principles" className="wrap home-section">
        <div className="home-section-head">
          <span className="eyebrow">$ cat PRINCIPLES.md</span>
          <h2>개발 원칙</h2>
          <p className="muted">AI와 같이 개발하면서 지키는 기준 여섯 가지입니다.</p>
          <Link to="/principles" className="home-more">원칙별 사례까지 자세히 보기</Link>
        </div>
        <ol className="home-principles">
          {PRINCIPLES.map((p, i) => (
            <li key={p.id}>
              <Link to={`/principles#${p.id}`}>
                <span className="mono">{String(i + 1).padStart(2, "0")}</span>
                <strong>{p.title}</strong>
              </Link>
            </li>
          ))}
        </ol>
      </section>

      <section id="capability" className="wrap home-section">
        <div className="home-section-head">
          <span className="eyebrow">SELECT * FROM capability;</span>
          <h2>전문성</h2>
          <p className="muted">다룰 줄 아는 기술과, 그걸로 실제로 만든 결과를 한 줄에 붙였습니다.</p>
        </div>
        <table className="home-caps">
          <thead>
            <tr>
              <th scope="col">area</th>
              <th scope="col">stack</th>
              <th scope="col">evidence</th>
            </tr>
          </thead>
          <tbody>
            {CAPABILITIES.map((r) => (
              <tr key={r.area} id={`cap-${r.key}`}>
                <th scope="row">{r.area}</th>
                <td className="muted">{r.stack}</td>
                <td>{r.evidence}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="wrap home-section home-section--apps">
        <div className="home-section-head">
          <span className="eyebrow">ls apps/ | head -2</span>
          <h2>사이드 프로젝트</h2>
        </div>
        <div className="home-cards">
          {total === 0 && <p className="home-card home-card--empty">{EMPTY}</p>}
          {pinned && <AppCard app={pinned} primary />}
          {latest && <AppCard app={latest} primary={false} />}
          <Link to="/apps" className="home-card home-card--all">
            <span className="mono muted">apps/</span>
            <strong>전체 {total}개 보기</strong>
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}
